<template>
  <component 
    :is="link ? 'a' : 'div'" 
    :href="link" 
    :target="link ? '_blank' : undefined"
    class="outline-1 shadow outline outline-n-container rounded-xl bg-n-solid-2 p-4 block" 
    :class="containerClass" 
    @click="handleClick"
  >
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center">
        <span :class="[icon, iconColorClass, 'mr-2 text-xl']"></span>
        <h4 class="text-base font-semibold m-0 text-n-slate-12">{{ title }}</h4>
      </div>
    </div>
    <div :class="['mt-3 mb-1 text-3xl font-bold', valueColorClass]">
      {{ formattedValue }}
      <span v-if="suffix" class="text-lg text-n-slate-9">{{ suffix }}</span>
    </div>
    <div v-if="comparison !== null" class="mt-2 space-y-0.5">
      <div class="flex items-center gap-1 text-sm font-medium" :class="comparisonClass">
        <span v-if="comparison !== 0" :class="comparison > 0 ? 'i-lucide-trending-up' : 'i-lucide-trending-down'" class="text-base" />
        {{ Math.abs(comparison) }}% a {{ comparison > 0 ? 'mais' : 'menos' }} que o período anterior
      </div>
      <div v-if="comparisonValue !== null" class="text-xs text-n-slate-10">
        {{ comparisonValue.toLocaleString() }} {{ comparisonUnit }} no período anterior
      </div>
    </div>
    <div v-if="subtitle" class="text-base mt-2 text-n-slate-11">
      {{ subtitle }}
    </div>
  </component>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  icon: {
    type: String,
    required: true
  },
  color: {
    type: String,
    default: 'woot'
  },
  title: {
    type: String,
    required: true
  },
  value: {
    type: [Number, String],
    required: true
  },
  suffix: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  },
  clickable: {
    type: Boolean,
    default: false
  },
  format: {
    type: String,
    default: 'number' // 'number', 'decimal', 'none'
  },
  link: {
    type: String,
    default: null
  },
  linkText: {
    type: String,
    default: ''
  },
  comparison: {
    type: Number,
    default: null
  },
  comparisonValue: {
    type: Number,
    default: null
  },
  comparisonUnit: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['click']);

const formattedValue = computed(() => {
  if (props.format === 'decimal') {
    return typeof props.value === 'number' ? props.value.toFixed(2) : props.value;
  } else if (props.format === 'number') {
    return typeof props.value === 'number' ? props.value.toLocaleString() : props.value;
  }
  return props.value;
});

const containerClass = computed(() => {
  if (props.link) {
    return 'no-underline hover:bg-n-slate-1 dark:hover:bg-n-solid-3 hover:outline-woot-500 transition-all cursor-pointer';
  }
  return props.clickable ? 'cursor-pointer hover:bg-n-slate-1 dark:hover:bg-n-solid-3 hover:outline-woot-500 transition-all' : '';
});

// Map color props to static Tailwind classes
const iconColorClass = computed(() => {
  const colorMap = {
    'woot': 'text-woot-600',
    'blue': 'text-woot-600',
    'green': 'text-green-600',
    'purple': 'text-purple-600',
    'amber': 'text-amber-600',
    'red': 'text-red-600',
    'yellow': 'text-yellow-600',
    'indigo': 'text-indigo-600'
  };
  return colorMap[props.color] || 'text-woot-600';
});

const valueColorClass = computed(() => {
  const colorMap = {
    'woot': 'text-woot-900 dark:text-woot-400',
    'blue': 'text-woot-900 dark:text-woot-400',
    'green': 'text-green-900 dark:text-green-400',
    'purple': 'text-purple-900 dark:text-purple-400',
    'amber': 'text-amber-900 dark:text-amber-400',
    'red': 'text-red-700 dark:text-red-400',
    'yellow': 'text-yellow-900 dark:text-yellow-400',
    'indigo': 'text-indigo-900 dark:text-indigo-400'
  };
  return colorMap[props.color] || 'text-woot-900 dark:text-woot-400';
});

const comparisonClass = computed(() => {
  if (props.comparison === null || props.comparison === 0) return 'text-n-slate-11';
  return props.comparison > 0
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400';
});

const handleClick = () => {
  if (props.clickable) {
    emit('click');
  }
};
</script>